// Berechnungszeile und „Limit setzen“ der Lead-Listen (Agent 10B, Runde 10): reine Logik ohne React, mit Node prüfbar
//   cd crm/web && node --test src/components/liste/tests/berechnung.test.mjs
//
// Die Berechnungszeile steht unter der Tabelle (wie „+ Calculation“ in Close): je Spalte eine Rechenart, gerechnet über alle Treffer der
// Liste (nicht nur die geladenen) vom Server (`POST /api/listen-berechnung/leads`, API.md Runde 10). Die Wahl wird je Nutzer und Liste
// in der gemerkten Ansicht gespeichert (`einstellungen.berechnung`). Das Limit („Set limit“) ist `results_limit` der Abfrage.

/** Rechenarten. Dieselben Schlüssel kennt der Server (`services/listen_berechnung.py`, ein Test vergleicht beide). */
export type BerechnungsFunktion =
  | "count_empty"
  | "count_not_empty"
  | "percent_empty"
  | "percent_not_empty"
  | "count_unique"
  | "sum"
  | "avg"
  | "median"
  | "min"
  | "max"
  | "earliest"
  | "latest";

const BASIS: BerechnungsFunktion[] = ["count_empty", "count_not_empty", "percent_empty", "percent_not_empty", "count_unique"];
const ZAHLEN: BerechnungsFunktion[] = ["sum", "avg", "median", "min", "max"];
const ZEITEN: BerechnungsFunktion[] = ["earliest", "latest"];

/** Welche Rechenarten zu welchem Spaltentyp (Katalog der Lead-Listen) gehören. Symbolspalten (Telefon/E-Mail) haben keine. */
export const FUNKTIONEN_JE_TYP: Record<string, BerechnungsFunktion[]> = {
  text: BASIS,
  wahl: BASIS,
  nutzer: BASIS,
  status: BASIS,
  enum: BASIS,
  zahl: [...BASIS, ...ZAHLEN],
  dauer: [...BASIS, ...ZAHLEN],
  datum: [...BASIS, ...ZEITEN],
  zeit: [...BASIS, ...ZEITEN],
};

export const FUNKTION_NAME: Record<BerechnungsFunktion, string> = {
  count_empty: "Anzahl leer",
  count_not_empty: "Anzahl gefüllt",
  percent_empty: "Prozent leer",
  percent_not_empty: "Prozent gefüllt",
  count_unique: "Verschiedene Werte",
  sum: "Summe",
  avg: "Durchschnitt",
  median: "Median",
  min: "Minimum",
  max: "Maximum",
  earliest: "Frühestes",
  latest: "Spätestes",
};

/** Kurzform für die Zeile unter der Tabelle (Platz ist knapp). */
export const FUNKTION_KURZ: Record<BerechnungsFunktion, string> = {
  count_empty: "Leer",
  count_not_empty: "Gefüllt",
  percent_empty: "Leer",
  percent_not_empty: "Gefüllt",
  count_unique: "Verschieden",
  sum: "Summe",
  avg: "Ø",
  median: "Median",
  min: "Min",
  max: "Max",
  earliest: "Früh.",
  latest: "Spät.",
};

/** Gruppen im Menü: Überschrift und Rechenarten (nur die zum Typ passenden werden gezeigt). */
export const FUNKTIONS_GRUPPEN: { titel: string; funktionen: BerechnungsFunktion[] }[] = [
  { titel: "Leer und gefüllt", funktionen: ["count_empty", "count_not_empty", "percent_empty", "percent_not_empty", "count_unique"] },
  { titel: "Zahlen", funktionen: ["sum", "avg", "median", "min", "max"] },
  { titel: "Zeit", funktionen: ["earliest", "latest"] },
];

export function funktionenFuer(typ: string | undefined): BerechnungsFunktion[] {
  return (typ && FUNKTIONEN_JE_TYP[typ]) || [];
}

export function istFunktion(wert: unknown): wert is BerechnungsFunktion {
  return typeof wert === "string" && wert in FUNKTION_NAME;
}

/** Wahl je Spalte: { spaltenschluessel: funktion } */
export type BerechnungsWahl = Record<string, BerechnungsFunktion>;

/** Gespeicherte Wahl (aus `einstellungen.berechnung`) lesen. Unbekanntes, Kaputtes und Unpassendes fällt weg. `typVon` liefert den Typ
 *  der Spalte (undefined = Spalte unbekannt, die Wahl fällt weg). `gezeigt`: nur Spalten, die gerade in der Liste stehen. */
export function wahlLesen(roh: unknown, typVon: (key: string) => string | undefined, gezeigt?: ReadonlySet<string>): BerechnungsWahl {
  const aus: BerechnungsWahl = {};
  if (!roh || typeof roh !== "object" || Array.isArray(roh)) return aus;
  for (const [key, f] of Object.entries(roh as Record<string, unknown>)) {
    if (!istFunktion(f)) continue;
    if (gezeigt && !gezeigt.has(key)) continue;
    if (!funktionenFuer(typVon(key)).includes(f)) continue;
    aus[key] = f;
  }
  return aus;
}

/** Neue Wahl nach dem Setzen oder Entfernen (`null`) einer Rechenart; leer = null (dann entfernt das Speichern den Schlüssel). */
export function wahlAendern(alt: BerechnungsWahl, key: string, funktion: BerechnungsFunktion | null): BerechnungsWahl | null {
  const neu: BerechnungsWahl = { ...alt };
  if (funktion === null) delete neu[key];
  else neu[key] = funktion;
  return Object.keys(neu).length ? neu : null;
}

/** Ergebnis einer Spalte (Antwort des Servers). */
export interface BerechnungsErgebnis {
  funktion: BerechnungsFunktion;
  wert: number | string | null;
  art: "anzahl" | "prozent" | "zahl" | "dauer" | "datum" | "zeit";
}

const ZAHL = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });
const PROZENT = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 1 });
const DATUM = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric" });
const ZEIT = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function zwei(n: number): string {
  return String(n).padStart(2, "0");
}

/** Sekunden als Dauer: 45 Sek. · 2:35 Min. · 1:05:09 Std. (wie die Dauer in der Anrufliste). */
export function dauerAlsText(sek: number): string {
  const g = Math.round(sek);
  if (g < 60) return `${g} Sek.`;
  const h = Math.floor(g / 3600);
  const m = Math.floor((g % 3600) / 60);
  const s = g % 60;
  return h ? `${h}:${zwei(m)}:${zwei(s)} Std.` : `${m}:${zwei(s)} Min.`;
}

/** Wert der Berechnung so, wie er unter der Spalte steht. `null` heißt „nichts zu rechnen“ und erscheint als Strich. */
export function ergebnisText(e: BerechnungsErgebnis | null | undefined): string {
  if (!e || e.wert === null || e.wert === undefined) return "–";
  switch (e.art) {
    case "anzahl":
      return typeof e.wert === "number" ? e.wert.toLocaleString("de-DE") : String(e.wert);
    case "prozent":
      return typeof e.wert === "number" ? `${PROZENT.format(e.wert)} %` : String(e.wert);
    case "zahl":
      return typeof e.wert === "number" ? ZAHL.format(e.wert) : String(e.wert);
    case "dauer":
      return typeof e.wert === "number" ? dauerAlsText(e.wert) : String(e.wert);
    case "datum": {
      const d = new Date(`${String(e.wert).slice(0, 10)}T12:00:00Z`);
      return Number.isNaN(d.getTime()) ? String(e.wert) : DATUM.format(d);
    }
    case "zeit": {
      const d = new Date(String(e.wert));
      return Number.isNaN(d.getTime()) ? String(e.wert) : ZEIT.format(d);
    }
  }
}

/** Beschriftung der Zelle: „Summe 15“ als Text für Tooltip und Vorlesen. */
export function ergebnisLang(spalte: string, e: BerechnungsErgebnis | null | undefined, funktion: BerechnungsFunktion): string {
  return `${spalte}: ${FUNKTION_NAME[funktion]} ${ergebnisText(e)}`;
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Limit („Set limit“)
// ---------------------------------------------------------------------------------------------------------------------------------

export const LIMIT_MAX = 100_000;
/** Vorschläge im Dialog (Power Dialer: ein Tagesvorrat). */
export const LIMIT_VORSCHLAEGE = [25, 50, 100, 250, 500];

export type LimitPruefung = { ok: true; limit: number | null } | { ok: false; meldung: string };

/** Eingabe des Limits lesen: leer = kein Limit; sonst eine ganze Zahl von 1 bis 100.000 (Punkt und Leerzeichen als Tausendertrenner egal). */
export function limitLesen(eingabe: string): LimitPruefung {
  const t = eingabe.trim().replace(/[.\s]/g, "");
  if (!t) return { ok: true, limit: null };
  if (!/^\d+$/.test(t)) return { ok: false, meldung: "Bitte eine ganze Zahl eingeben, zum Beispiel 100." };
  const n = Number(t);
  if (n < 1) return { ok: false, meldung: "Das Limit muss mindestens 1 sein. Ohne Limit das Feld leer lassen." };
  if (n > LIMIT_MAX) return { ok: false, meldung: `Höchstens ${LIMIT_MAX.toLocaleString("de-DE")}.` };
  return { ok: true, limit: n };
}

interface AbfrageMitLimit {
  query?: unknown;
  sort?: unknown[] | null;
  results_limit?: number | null;
}

/** Abfrage mit gesetztem (oder entferntem) Limit; alles andere bleibt. */
export function mitLimit<T extends AbfrageMitLimit>(abfrage: T, limit: number | null): T {
  return { ...abfrage, results_limit: limit };
}

/** Limit der Abfrage (nur eine Zahl ≥ 1 zählt). */
export function limitVon(abfrage: AbfrageMitLimit | null | undefined): number | null {
  const l = abfrage?.results_limit;
  return typeof l === "number" && Number.isFinite(l) && l >= 1 ? Math.floor(l) : null;
}

/** Text neben der Zahl: „Limit setzen“ ohne Limit, sonst „Limit 100“. */
export function limitKnopfText(limit: number | null): string {
  return limit === null ? "Limit setzen" : `Limit ${limit.toLocaleString("de-DE")}`;
}

/** Zusatz hinter der Trefferzahl, wenn ein Limit die Liste kürzt: „von 5.312“. Ohne bekannte Gesamtzahl oder wenn das Limit nichts kürzt: leer. */
export function limitVonText(limit: number | null, gesamt: number | null): string {
  if (limit === null || gesamt === null || gesamt <= limit) return "";
  return `von ${gesamt.toLocaleString("de-DE")}`;
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Sortierung der Leads-Liste in die Abfrage (damit „die ersten N“ dieselben sind, die man in der Liste sieht)
// ---------------------------------------------------------------------------------------------------------------------------------

export interface SortSchluessel {
  key: string;
  ab: boolean;
}

interface FeldRef {
  type: string;
  object_type?: string;
  field_name?: string;
  custom_field_id?: string;
}

/** Sortierfeld des Filter-Katalogs (`FilterKatalogFeld.feld`) -> Schlüssel der Lead-Listen (`display_name`, `custom.cf_…`, `num_calls`). */
function schluesselVon(feld: FeldRef | null | undefined): string | null {
  if (!feld) return null;
  if (feld.type === "custom_field" && feld.custom_field_id) return `custom.${feld.custom_field_id.replace(/^custom\./, "")}`;
  if (feld.type === "regular_field" && feld.field_name) return feld.field_name;
  return null;
}

/** Eigene Sortierung der Liste als Sortierung der Abfrage (Close-Form). `felder`: sortierbare Felder des Filter-Katalogs. Ist ein Feld dort nicht
 *  zu finden, gibt es `null` (dann bleibt die Sortierung der Abfrage, wie sie ist). */
export function sortAlsAbfrage(sortierung: SortSchluessel[], felder: { feld: FeldRef | null }[]): unknown[] | null {
  if (!sortierung.length) return null;
  const aus: unknown[] = [];
  for (const s of sortierung) {
    const f = felder.find((x) => schluesselVon(x.feld) === s.key);
    if (!f?.feld) return null;
    aus.push({ direction: s.ab ? "desc" : "asc", field: f.feld });
  }
  return aus;
}

/** Reihenfolge der Liste als Satzteil: „Geändert absteigend, dann Name aufsteigend“. */
export function sortBeschreibung(sortierung: SortSchluessel[], label: (key: string) => string): string {
  if (!sortierung.length) return "wie die Liste sortiert ist";
  return sortierung.map((s) => `${label(s.key)} ${s.ab ? "absteigend" : "aufsteigend"}`).join(", dann ");
}
